import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, photoUrl, errText } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import { ReportUserDialog } from "../ReportUserDialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Trash2, Pencil, Send, Inbox, CheckCircle2, Ban, Flag, BellOff, Bell, CheckCheck } from "lucide-react";
import { Empty, Loader } from "./shared";
import { MessageText } from "./MessageText";

export const MSG_REPORT_REASONS = [
  { en: "Scam/fraud", fr: "Arnaque/fraude" },
  { en: "Spam", fr: "Pourriel" },
  { en: "Harassment/abuse", fr: "Harcèlement/abus" },
  { en: "Inappropriate content", fr: "Contenu inapproprié" },
  { en: "Other", fr: "Autre" },
];

export function Messages({ user, params, setParams }) {
  const { t, lang } = useLanguage();
  const [convs, setConvs] = useState(null);
  const [activeId, setActiveId] = useState(params.get("c") || null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [clearConv, setClearConv] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [reportNote, setReportNote] = useState("");

  const reloadConvs = () => api.get("/conversations").then((r) => setConvs(r.data));
  useEffect(() => {
    api.get("/conversations").then((r) => { setConvs(r.data); if (!activeId && r.data[0]) setActiveId(r.data[0].id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadThread = useCallback(() => {
    if (!activeId) return;
    api.get(`/conversations/${activeId}/messages`).then((r) => setThread(r.data));
  }, [activeId]);
  useEffect(() => { loadThread(); }, [loadThread]);

  // Light polling so read receipts + incoming messages stay fresh while a thread is open.
  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      api.get(`/conversations/${activeId}/messages`).then((r) => setThread(r.data)).catch(() => {});
      api.get("/conversations").then((r) => setConvs(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [activeId]);

  const toggleMute = async () => {
    if (!conv) return;
    try {
      if (conv.muted) { await api.delete(`/conversations/${activeId}/mute`); toast.success("Notifications unmuted"); }
      else { await api.post(`/conversations/${activeId}/mute`); toast.success("Conversation muted"); }
      loadThread(); reloadConvs();
    } catch (e) { toast.error(errText(e)); }
  };

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/conversations/${activeId}/messages`, { message: text });
      setText(""); loadThread(); reloadConvs();
    } catch (e) { toast.error(errText(e)); }
  };

  const delMsg = async (id) => {
    try { await api.delete(`/conversations/${activeId}/messages/${id}`); setConfirmDel(null); loadThread(); reloadConvs(); }
    catch (e) { toast.error(errText(e)); }
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try { await api.put(`/conversations/${activeId}/messages/${id}`, { message: editText }); setEditingId(null); setEditText(""); loadThread(); reloadConvs(); }
    catch (e) { toast.error(errText(e)); }
  };

  const doClear = async () => {
    try {
      const id = clearConv;
      await api.delete(`/conversations/${id}`);
      setClearConv(null);
      if (activeId === id) { setActiveId(null); setThread(null); }
      reloadConvs();
      toast.success("Conversation removed from your list");
    } catch (e) { toast.error(errText(e)); }
  };

  const submitReport = async () => {
    if (!reportReason) return toast.error("Pick a reason");
    try {
      await api.post(`/conversations/${activeId}/messages/${reportFor}/report`, { reason: reportReason, details: reportNote.trim() });
      setReportFor(null); setReportReason(""); setReportNote("");
      toast.success("Report sent to our moderators. Thank you!");
    } catch (e) { toast.error(errText(e)); }
  };

  const toggleBlock = async () => {
    const ou = thread.conversation.other_user;
    try {
      if (thread.conversation.other_blocked) { await api.delete(`/users/${ou.user_id}/block`); toast.success(`Unblocked ${ou.name}`); }
      else { await api.post(`/users/${ou.user_id}/block`); toast.success(`Blocked ${ou.name}. They can no longer message you.`); }
      loadThread(); reloadConvs();
    } catch (e) { toast.error(errText(e)); }
  };

  if (!convs) return <Loader />;
  if (!convs.length) return <Empty icon={Inbox} text={t("d_no_messages")} />;

  const conv = thread?.conversation;
  const canBlock = conv && conv.kind !== "broadcast" && !conv.is_staff && conv.other_role === "user";
  const canMute = conv && conv.kind !== "broadcast";
  const canReportUser = conv && conv.kind !== "broadcast" && !conv.is_staff && conv.other_role === "user";
  // Read receipt: id of my most recent non-deleted message that the other party has read.
  const lastReadMineId = (() => {
    if (!thread) return null;
    const readMine = thread.messages.filter((m) => m.sender_id === user.user_id && !m.deleted && m.read);
    const last = readMine[readMine.length - 1];
    return last ? last.id : null;
  })();

  return (
    <div className="grid md:grid-cols-3 gap-4 bg-white rounded-2xl border border-slate-200/80 overflow-hidden" style={{ minHeight: 480 }}>
      <div className="border-r border-slate-100 overflow-y-auto max-h-[560px]">
        {convs.map((c) => (
          <div key={c.id} className={`group/conv relative border-b border-slate-100 ${activeId === c.id ? "bg-[#FFF4F0]" : ""}`}>
            <button onClick={() => setActiveId(c.id)} data-testid={`conversation-${c.id}`}
              className="w-full text-left p-4 flex gap-3 hover:bg-slate-50 transition-colors">
              <Avatar className="h-10 w-10 shrink-0"><AvatarImage src={photoUrl(c.other_user.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{c.other_user.name?.[0]}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-1"><p className="font-semibold text-[#12234A] text-sm truncate flex items-center gap-1">{c.other_user.name}{c.is_staff && <span className="text-[9px] bg-[#12234A] text-white px-1.5 py-0.5 rounded-full">TEAM</span>}{c.muted && <BellOff size={12} className="text-slate-400 shrink-0" data-testid={`muted-indicator-${c.id}`} />}</p>{c.unread > 0 && (c.muted ? <span className="h-2 w-2 rounded-full bg-slate-300 shrink-0" data-testid={`muted-unread-dot-${c.id}`} /> : <span className="bg-[#F5551E] text-white text-[10px] rounded-full h-5 min-w-5 px-1 flex items-center justify-center">{c.unread}</span>)}</div>
                <p className="text-xs text-slate-400 truncate">{c.listing_title}</p>
                <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_message}</p>
              </div>
            </button>
            <button onClick={(e) => { e.stopPropagation(); setClearConv(c.id); }} data-testid={`clear-conversation-${c.id}`}
              title="Delete conversation" className="absolute top-2 right-2 opacity-0 group-hover/conv:opacity-100 transition-opacity h-7 w-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-200">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <div className="md:col-span-2 flex flex-col max-h-[560px]">
        {thread ? (
          <>
            <div className="p-4 border-b border-slate-100 flex items-center gap-3">
              {conv.kind && conv.kind !== "listing" ? (
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="h-10 w-10"><AvatarImage src={photoUrl(conv.other_user.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{conv.other_user.name?.[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0"><p className="font-semibold text-[#12234A] text-sm truncate">{conv.other_user.name}</p><p className="text-xs text-slate-400">{conv.kind === "broadcast" ? t("d_announcement") : t("d_direct_message")}</p></div>
                </div>
              ) : (
                <Link to={`/listing/${conv.listing_id}`} className="flex items-center gap-3 min-w-0">
                  {conv.listing_photo && <img src={photoUrl(conv.listing_photo)} alt="" className="h-10 w-10 rounded-lg object-cover" />}
                  <div className="min-w-0"><p className="font-semibold text-[#12234A] text-sm truncate">{conv.listing_title}</p><p className="text-xs text-slate-400">{t("d_with")} {conv.other_user.name}</p></div>
                </Link>
              )}
              {(canMute || canBlock || canReportUser) && (
                <div className="ml-auto flex items-center gap-1.5">
                  {canMute && (
                    <button onClick={toggleMute} data-testid="mute-conversation-btn" title={conv.muted ? "Unmute notifications" : "Mute notifications"}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${conv.muted ? "border-[#F5551E]/30 text-[#F5551E] bg-[#FFF4F0]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                      {conv.muted ? <><Bell size={13} /> {t("d_unmute")}</> : <><BellOff size={13} /> {t("d_mute")}</>}
                    </button>
                  )}
                  {canReportUser && (
                    <ReportUserDialog userId={conv.other_user.user_id} userName={conv.other_user.name} trigger={
                      <button data-testid="report-user-btn" title="Report user" className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 text-slate-500 hover:border-amber-200 hover:text-amber-600 transition-colors inline-flex items-center gap-1.5 cursor-pointer"><Flag size={13} /> {t("d_report")}</button>
                    } />
                  )}
                  {canBlock && (
                    <button onClick={toggleBlock} data-testid="block-user-btn" className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${conv.other_blocked ? "border-emerald-200 text-emerald-600 hover:bg-emerald-50" : "border-slate-200 text-slate-500 hover:border-red-200 hover:text-red-500"}`}>
                      {conv.other_blocked ? t("d_unblock") : t("d_block")}
                    </button>
                  )}
                </div>
              )}
            </div>
            {conv.other_blocked && (
              <div className="px-4 py-2 bg-red-50 text-red-600 text-xs flex items-center gap-1.5" data-testid="blocked-banner"><Ban size={13} /> {t("d_blocked_banner")}</div>
            )}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-thread">
              {thread.messages.map((m) => {
                const mine = m.sender_id === user.user_id;
                const canDelete = !m.deleted && (!m.sender_is_staff || user.role === "admin");
                const canEdit = mine && !m.deleted;
                const canReport = !mine && !m.deleted;
                const imgs = (m.images && m.images.length) ? m.images : (m.image ? [m.image] : []);
                if (editingId === m.id) {
                  return (
                    <div key={m.id} className="flex justify-end gap-2 items-center" data-testid={`edit-message-${m.id}`}>
                      <Input value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEdit(m.id)} data-testid={`edit-input-${m.id}`} className="rounded-xl max-w-[70%]" autoFocus />
                      <Button onClick={() => saveEdit(m.id)} data-testid={`edit-save-${m.id}`} className="rounded-xl h-9 px-3 bg-[#F5551E] hover:bg-[#E04812] text-white"><CheckCircle2 size={15} /></Button>
                      <Button onClick={() => { setEditingId(null); setEditText(""); }} variant="outline" className="rounded-xl h-9 px-3">Cancel</Button>
                    </div>
                  );
                }
                return (
                  <div key={m.id}>
                  <div className={`group flex items-center gap-1.5 ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                    {mine && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        {canEdit && <button onClick={() => { setEditingId(m.id); setEditText(m.body); }} data-testid={`edit-message-btn-${m.id}`} className="text-slate-400 hover:text-[#12234A]" title="Edit"><Pencil size={13} /></button>}
                        {canDelete && <button onClick={() => setConfirmDel(m.id)} data-testid={`delete-message-${m.id}`} className="text-slate-400 hover:text-red-500" title="Delete"><Trash2 size={14} /></button>}
                      </div>
                    )}
                    {m.deleted ? (
                      <div className="max-w-[75%] px-4 py-2 rounded-2xl text-sm bg-slate-100 text-slate-400 italic flex items-center gap-1.5"><Ban size={13} /> {t("d_message_deleted")}</div>
                    ) : (
                      <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm whitespace-pre-line ${mine ? "bg-[#F5551E] text-white rounded-br-sm" : "bg-slate-100 text-[#12234A] rounded-bl-sm"}`}>
                        {imgs.length > 0 && (
                          <div className={`grid gap-1.5 mb-1.5 ${imgs.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                            {imgs.map((im, ix) => (
                              <a key={ix} href={photoUrl(im)} target="_blank" rel="noopener noreferrer" data-testid={`message-image-link-${m.id}-${ix}`}>
                                <img src={photoUrl(im)} alt="attachment" data-testid={`message-image-${m.id}-${ix}`} className="rounded-xl w-full max-h-60 object-cover cursor-pointer border border-black/5" />
                              </a>
                            ))}
                          </div>
                        )}
                        {Array.isArray(m.photos) && m.photos.length > 0 && (
                          <div className={`grid gap-1.5 mb-2 ${m.photos.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid={`message-photos-${m.id}`}>
                            {m.photos.map((p) => (
                              <a key={p} href={photoUrl(p)} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-xl">
                                <img src={photoUrl(p)} alt="" loading="lazy" className="w-full h-36 object-cover hover:scale-105 transition-transform duration-300" />
                              </a>
                            ))}
                          </div>
                        )}
                        <MessageText m={m} mine={mine} lang={lang} convId={activeId} />
                        {m.edited && <span className={`ml-1.5 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`} data-testid={`edited-tag-${m.id}`}>{t("d_edited")}</span>}
                        {m.cta_label && m.cta_url && (
                          m.cta_url.startsWith("http") ? (
                            <a href={m.cta_url} target="_blank" rel="noopener noreferrer" onClick={() => m.broadcast_id && api.post(`/broadcasts/${m.broadcast_id}/cta-click`).catch(() => {})} data-testid={`message-cta-${m.id}`} className={`mt-2 block text-center font-semibold text-sm px-4 py-2 rounded-xl transition-colors ${mine ? "bg-white text-[#F5551E] hover:bg-white/90" : "bg-[#F5551E] text-white hover:bg-[#E04812]"}`}>{m.cta_label}</a>
                          ) : (
                            <Link to={m.cta_url} onClick={() => m.broadcast_id && api.post(`/broadcasts/${m.broadcast_id}/cta-click`).catch(() => {})} data-testid={`message-cta-${m.id}`} className={`mt-2 block text-center font-semibold text-sm px-4 py-2 rounded-xl transition-colors ${mine ? "bg-white text-[#F5551E] hover:bg-white/90" : "bg-[#F5551E] text-white hover:bg-[#E04812]"}`}>{m.cta_label}</Link>
                          )
                        )}
                      </div>
                    )}
                    {!mine && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        {canReport && <button onClick={() => { setReportFor(m.id); setReportReason(""); setReportNote(""); }} data-testid={`report-message-${m.id}`} className="text-slate-400 hover:text-amber-500" title="Report"><Flag size={13} /></button>}
                        {canDelete && <button onClick={() => setConfirmDel(m.id)} data-testid={`delete-message-${m.id}`} className="text-slate-400 hover:text-red-500" title="Delete"><Trash2 size={14} /></button>}
                      </div>
                    )}
                  </div>
                  {m.id === lastReadMineId && (
                    <div className="flex justify-end mt-0.5 pr-1" data-testid="read-receipt-seen"><span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5"><CheckCheck size={12} className="text-[#F5551E]" /> {t("d_seen")}</span></div>
                  )}
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t border-slate-100 flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="message-input" placeholder={conv.other_blocked ? t("d_unblock_to_send") : t("d_type_message")} disabled={conv.other_blocked} className="rounded-xl" />
              <Button onClick={send} disabled={conv.other_blocked} data-testid="send-message-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white px-4"><Send size={16} /></Button>
            </div>
          </>
        ) : <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-2 p-8"><Inbox size={40} /><p className="text-sm">{t("d_select_conv")}</p></div>}
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="delete-message-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("d_dm_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("d_dm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("d_cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => delMsg(confirmDel)} data-testid="confirm-delete-message" className="bg-red-600 hover:bg-red-700">{t("d_delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!clearConv} onOpenChange={(o) => !o && setClearConv(null)}>
        <AlertDialogContent data-testid="clear-conversation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("d_cc_title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("d_cc_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("d_cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doClear} data-testid="confirm-clear-conversation" className="bg-red-600 hover:bg-red-700">{t("d_delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!reportFor} onOpenChange={(o) => !o && setReportFor(null)}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="report-message-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Flag size={18} className="text-amber-500" /> {t("d_rm_title")}</DialogTitle>
            <DialogDescription className="text-slate-500">{t("d_rm_desc")}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2" data-testid="report-reasons">
            {MSG_REPORT_REASONS.map((r) => (
              <button key={r.en} onClick={() => setReportReason(r.en)} data-testid={`report-reason-${r.en.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${reportReason === r.en ? "bg-amber-500 border-amber-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-amber-400 hover:text-amber-600"}`}>{lang === "fr" ? r.fr : r.en}</button>
            ))}
          </div>
          <Textarea value={reportNote} onChange={(e) => setReportNote(e.target.value)} data-testid="report-note" rows={3} maxLength={500} placeholder={t("d_rm_note_ph")} className="rounded-xl resize-none" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setReportFor(null)} className="rounded-xl">{t("d_cancel")}</Button>
            <Button onClick={submitReport} data-testid="report-submit" className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white gap-1.5"><Flag size={15} /> {t("d_rm_send")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
